import { inject, injectable } from 'inversify';

import { AnimatePageModel, PageTimeline } from '@desygner/api-animations-models';
import {
  callInAnimation,
  callOutAnimation,
  isAnimationValid,
} from '@desygner/ui-common-animation-timeline';
import {
  Design,
  TYPES as PROJECT_SERVICE_TYPES,
  ProjectStoreInjector,
} from '@desygner/ui-features-projects';

import { TYPES } from '../../inversify.types';
import { IViewerStoreInjector } from '../../store';

@injectable()
export class AnimationManager {
  constructor(
    @inject(TYPES.Store.Viewer) private viewerStore: IViewerStoreInjector,
    @inject(PROJECT_SERVICE_TYPES.ProjectsStore)
    private readonly projectsStore: ProjectStoreInjector
  ) {}

  private timeline: PageTimeline | null = null;
  private canvas: Element | null = null;
  private readonly defaultAnimationMetadata: AnimatePageModel = {
    animation: 'no_animation',
  };

  private render = () => {
    const content = this.getCurrentPageContent();

    if (!content) return null;

    const shadowRoot = document.querySelector('[data-shadow-root-container]')?.shadowRoot;
    const svgContainer = shadowRoot?.querySelector('[data-svg-container]');

    this.removeCanvas();

    // Find the StyledSvgContainer inside the wrapper div
    // DOM structure: StyledShadowDOMWrapper [data-svg-container] > div (ref wrapper) > StyledSvgContainer (div)
    const wrapperDiv = svgContainer?.querySelector('div');

    // The StyledSvgContainer is a div inside the wrapper
    // If it doesn't exist yet, we use the wrapper itself as the container
    let styledSvgContainer = wrapperDiv?.querySelector('div');

    if (!styledSvgContainer) {
      styledSvgContainer = wrapperDiv;
    }

    if (!styledSvgContainer) return null;

    styledSvgContainer.innerHTML = content;
    this.canvas = styledSvgContainer;

    return styledSvgContainer.querySelector('svg');
  };

  private shouldSkipAnimation(metadata?: AnimatePageModel) {
    return !metadata || !isAnimationValid(metadata) || this.pageMode !== 'single';
  }

  public animateIn = () => {
    const currentPage = this.viewerStore.getState().selectedPageNumber;
    const metadata = this.getCurrentPageMetadata(currentPage) ?? this.defaultAnimationMetadata;

    if (this.shouldSkipAnimation(metadata)) {
      return Promise.resolve();
    }

    const svg = this.render();

    if (!svg) {
      return Promise.resolve();
    }

    callInAnimation({
      animate: metadata,
      svg,
    });
  };

  private get pageMode() {
    return this.viewerStore.getState().pageMode;
  }

  public animateOut = () => {
    const currentPage = this.viewerStore.getState().selectedPageNumber;
    const metadata = this.getCurrentPageMetadata(currentPage) ?? this.defaultAnimationMetadata;

    if (this.shouldSkipAnimation(metadata)) {
      return Promise.resolve();
    }

    const svg = this.render();

    if (!svg) {
      return Promise.resolve();
    }

    const timeline = callOutAnimation({
      animate: metadata,
      svg,
    });

    this.timeline = timeline;

    if (!timeline) {
      return Promise.resolve();
    }

    return new Promise<void>((resolve) => {
      timeline.eventCallback('onReverseComplete', () => {
        return resolve();
      });

      timeline.eventCallback('onInterrupt', () => {
        return resolve();
      });
    });
  };

  private getCurrentPageContent = (): string | null => {
    const currentPageNumber = this.viewerStore.getState().selectedPageNumber;
    const project = this.projectsStore.getState().project.currentProject;
    const pages = this.projectsStore.getState().pages.allPages;

    if (!project || !project.pages || !project.pages[currentPageNumber - 1]) {
      return null;
    }

    const pageId = project.pages[currentPageNumber - 1].id;
    const page = pages[pageId.toString()];

    return page?.content || null;
  };

  private getCurrentPageMetadata = (pageNumber: number) => {
    const project = this.projectsStore.getState().project.currentProject;
    const id = project?.pages[pageNumber - 1].id;
    const metadata = (project as Design).metadata?.animation?.pages?.[Number(id)];

    return metadata;
  };

  private clearTimelines = () => {
    this.timeline?.kill();
    this.timeline = null;
  };

  private removeCanvas = () => {
    this.clearTimelines();
    if (this.canvas) {
      this.canvas.innerHTML = '';
    }
  };
}
